import React, { Component } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import Auth from '@aws-amplify/auth';
import {
    redirectToClient,
    getCurrentEnvironmentConfig,
    getStringBundleHost,
    loadCss,
    logger,
    redirectToSignInWithRedirectUri,
    logMissingUrlParams,
    shouldLogMissingUrlParams,
    getClientMetadata,
    redirectToChangeAccountPage,
    clearAppSession,
    interceptIamPost,
    isMobileOs
} from './util/AppUtil';
import { resolve } from 'react-resolver';
import { getAwsConfig, getCentralConfig, validateAndStartAppServerState } from "./service/AppService";
import queryString from 'query-string';
import { connect } from 'react-redux';
import {
    setQueryStringToState,
    setUserDetailsToState,
    setDynamicContent,
    setIsChromeIncognito,
    setCentralConfig,
    // loadABTestConfigComplexPassword,
    // loadABTestConfigSignUp,
    // loadABTestConfig
} from './actions/ApplicationActions';
import {
    REQUIRED_URL_PARAMS,
    DEFAULT_REQUIRED_URL_PARAMS,
    BRAND_UID_MAP,
    VALIDATION_CONSTRAINTS,
    SOCIAL_PROVIDER_NAME
} from './util/AppConstant';
import { SPLUNK_LABELS, logLoadTimeOfChunk, resetLoadTimeOfChunk } from './util/SplunkUtil';
import ToolbarTemplate from './component/page-templates/ToolbarTemplate';
import { isPersistedLogin } from './service/AmplifyConfig';
import ThemeController from './themes/ThemeController';
import ToolbarTemplateGuest from './component/page-templates/ToolbarTemplateGuest';
import ToolbarTemplateCostco from './component/page-templates/ToolbarTemplateCostco';
import ToolbarTemplateAmazon from './component/page-templates/ToolbarTemplateAmazon';
import ToolbarTemplateMarketing from './component/page-templates/ToolbarTemplateMarketing';
import SessionExpiryOverlay from './component/amazon/SessionExpiryOverlay';



class App extends Component {

    constructor(props) {
        super(props);
        // Check and refresh the migrate page if browser unable to figureout the query params(Specific to safari)
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) && window.location.pathname.match(/(\/migrate|\/changepassword|\/profile|\/elevate|\/resetPassword|\/logout|\/unlink)/) && !window.location.search) {
            if (!window.localStorage.getItem('pageRefresh')) {
                window.localStorage.setItem('pageRefresh', true);
                window.location.reload();
            }
        } else if (window.localStorage.getItem('pageRefresh')) {
            window.localStorage.removeItem('pageRefresh');
        }

        // Set state of the app redux store so we can access them from the store..
        const urlParams = queryString.parse(window.location.search);
        // queryString avoids special charectors. For costco signin written special case.
        if (urlParams.q && window.location.pathname.match(/(\/costcoHelloSignUp|\/costcoSignIn)/)) {
            const url = window.location.search.split("&");
            const encodedValue = url.find(param => param.includes("q=")).replace(/[?]/g, "");
            delete urlParams.q;
            urlParams.costcoencodedvalue = encodedValue.replace("q=","");
        }
        props.setQueryStringToState(urlParams);
        props.setCentralConfig(props.centralConfig);

        // USER-4552: Log a splunk message when we are missing URL params a page is expecting
        if (shouldLogMissingUrlParams(window.location.href)) {
            // Remove trailing '/'
            const pathname = window.location.pathname;
            const pathnameLen = pathname.length;
            const normalizedPathname = pathnameLen > 0 && pathname[pathnameLen - 1] === '/' ? pathname.slice(0, -1) : pathname;
            const requiredParams = REQUIRED_URL_PARAMS[normalizedPathname] || DEFAULT_REQUIRED_URL_PARAMS;
            logMissingUrlParams(urlParams, requiredParams.split(','));
        }


        // HACK: Check if Chrome is Incognito
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            navigator.storage.estimate().then((result) => {
                if (result.quota < (1024 * 1024 * 1024 * 1.5)) {
                    props.setIsChromeIncognito(true);
                }
            });
        }

        this.state = {
            validSession: true,
            userDetails: null,
            loadingSession: true,
            cssLoading: true,
            isLandscape: this.isMobileLandscape(),
            urlParams
        };

        this.loadMainCss();
        interceptIamPost({ 'Content-Type': 'application/json', "SFLY-Apikey": getCurrentEnvironmentConfig().resetPasswordApigeeApiKey });
    }

    /**
     * Infinite Loop Checker
     * Checks to see if the client app and the sso server are in a constant loop back and forth
     */
    infiniteLoopCheck() {
        const lastLoadInfo = window.localStorage.getItem('sflySsoLastLoad');
        if (lastLoadInfo) {
            const lastLoadInfoJson = JSON.parse(lastLoadInfo);
            if (Date.now() - lastLoadInfoJson.time < 2000) {
                if (lastLoadInfoJson.location === window.location.href) {
                    lastLoadInfoJson.count++;
                    lastLoadInfoJson.time = Date.now();
                    window.localStorage.setItem('sflySsoLastLoad', JSON.stringify(lastLoadInfoJson));

                    // After 3 or 4 loops we are going to logout and call it quits
                    if (lastLoadInfoJson.count > 3) {
                        lastLoadInfoJson.time = Date.now();
                        lastLoadInfoJson.location = window.location.href;
                        lastLoadInfoJson.count = 0;
                        window.localStorage.setItem('sflySsoLastLoad', JSON.stringify(lastLoadInfoJson));
                        window.location.assign('/logout');
                        return true;
                    }
                }
            } else {
                // Reset loop breaker if we feel there is no looping
                lastLoadInfoJson.time = Date.now();
                lastLoadInfoJson.location = window.location.href;
                lastLoadInfoJson.count = 0;
                window.localStorage.setItem('sflySsoLastLoad', JSON.stringify(lastLoadInfoJson));
            }
        } else {
            // Set initial loop breaker info
            const data = {
                time: Date.now(),
                location: window.location.href,
                count: 0
            };
            window.localStorage.setItem('sflySsoLastLoad', JSON.stringify(data));
        }

        return false;
    }

    async loadMainCss() {
        let brandHost = await getStringBundleHost();
        // Load the css file for a "brand". This file should include the look and feel
        const url = `${brandHost}/main.css`;
        loadCss(url, () => {
            this.setState({ cssLoading: false });
        }, () => {
            logger.error({
                appContext: {
                    msg: 'loadMainCss: error loading /main.css',
                    url
                }
            });
            this.setState({ cssLoading: false });
        });
    }

    isMobileLandscape = () => {
        const isLandscape = window.matchMedia("(orientation: landscape)").matches;
        const isMobile = isMobileOs();
        return isLandscape && isMobile;
      };
    
      handleOrientationChange = () => {
        let orientationState = this.isMobileLandscape();
        if(orientationState) {
            this.setState({ isLandscape:  orientationState});
        }
      };
    

    componentDidMount() {

        this.mediaQueryOrientation = window.matchMedia("(orientation: landscape)");
        this.mediaQueryOrientation.addEventListener('change', this.handleOrientationChange);
    
        this.mediaQueryMobile = window.matchMedia("(max-width: 768px)");
        this.mediaQueryMobile.addEventListener('change', this.handleOrientationChange);
        
        const urlParams = queryString.parse(window.location.search);
        // Check for infiniteLoops to the same URL before checking the rest
        if (!this.infiniteLoopCheck()) {
            // Let everything pass through to elevate and logout routes.
            if (window.location.pathname.match(/(\/logout|\/social\/callback|\/resetPassword)/)) {
                this.setState({
                    validSession: false,
                    loadingSession: false
                });
            } else {
                if (!urlParams.popupFormSubmittedSuccessfully) {
                    this.currentSession();
                }
            }
        }
        // Log javascript chunk load time
        logLoadTimeOfChunk(SPLUNK_LABELS.METRIC.LOAD_TIME_APP_CHUNK);

        // call AB testing
        // this.props.LoadABConfig();
        // this.props.LoadABConfigComplexPassword(urlParams.complexPassword);
        // this.props.LoadABConfigSignUp(urlParams.newSignUp);
        // this.props.LoadABConfigRecaptcha();
    }
    
    componentWillUnmount() {
        window.unregister && window.unregister();
        this.mediaQueryOrientation.removeEventListener('change', this.handleOrientationChange);
        this.mediaQueryMobile.removeEventListener('change', this.handleOrientationChange);
    }

    setUserDetails(session) {
        this.props.setUserDetailsToState(session.signInUserSession.idToken);
        this.setState({
            userDetails: session.signInUserSession.idToken.payload,
            validSession: false,
            loadingSession: false,
        });
    }

    /**
     * Checks for the current session if user is not logged in shows the signin page. 
     * if user is logged in and refresh token is valid redirects to the 'redirectUri' param or default redirection
     * else shows the signin screen.
     * 
     * @returns void
     */
    currentSession() {
        if (this.props.awsConfig.error) {
            this.setState({ error: this.props.awsConfig.error });
            return;
        }
        Auth.currentAuthenticatedUser()
            .then(async (session) => {
                if (window.location.pathname.match(/(\/costco|\/costcoVerifyEmail|\/costcoVerificationLinkSent|\/costcoSignUp|\/costcoHelloSignUp|\/costcoSignIn|\/costcoMembership)/) && !window.location.pathname.includes("/costcoCongrats") && !window.location.pathname.includes("/costcoCongratsTransfer")) {
                    this.setState({ validSession: false, loadingSession: false });
                    Auth.signOut();
                    clearAppSession(null,'CostcoMembership')
                    
                }
                  else if (!window.location.pathname.match(/(\/elevate|\/changepassword|\/requestyourcontact|\/profile|\/migrate|\/unlink|\/costcoCongrats|\/costcoCongratsTransfer|\/costco|\/welcomeCostco|\/welcomecostco|\/WelcomeCostco|\/amazoncongrats|\/amazonlinkingprovider|\/amazon|\/amazonOnboard)/)) {
                    await validateAndStartAppServerState(session.signInUserSession.refreshToken.token);
                    if (!window.location.pathname.match(/(\/federatedSignIn|\/linking)/)) {
                        if (session.signInUserSession.idToken.payload.user_type === 'GUEST' && (this.props.redirectUri || this.props.verifyguestaccount))
                            this.setUserDetails(session);
                        else
                            redirectToClient(this.props.redirectUri, 'SignIn', null, null,session.signInUserSession.clockDrift, session.signInUserSession.refreshToken, isPersistedLogin());
                    } else {
                        this.setUserDetails(session);
                    }
                } else {
                    // lets eventually use userDetails from redux.
                    this.setUserDetails(session);
                }
                if (window.location.pathname.match(/(\/elevate)/) && this.props.brand !== "sfly") {
                    const getNewRefreshToken = (result, refreshedDetails) => {
                        this.props.setUserDetailsToState(refreshedDetails.idToken);
                        this.setState({
                            userDetails: refreshedDetails.idToken.payload,
                            validSession: false,
                            loadingSession: false,
                        });
                    };
                    const clientMetadata = getClientMetadata(this.props.brand, this.props.theme);
                    session.refreshSession(session.signInUserSession.refreshToken, getNewRefreshToken, clientMetadata);
                }
            })
            .catch((error) => {
                // for profile update we need user to be signed in.
                if (window.location.pathname.match(/(\/profile)/)) {
                    redirectToSignInWithRedirectUri(window.location.href);
                } else {
                    this.setState({ validSession: false, loadingSession: false });
                }
            })
    }

    /**
     * Checks for the unmerged brand user if user unmerged it shows the respective brnad home page. 
     * USR-538
     * 
     * @returns void
     */

    navigateToBrandPage() {
        if (this.props.brand !== "sfly" && this.state?.userDetails && !this.state.userDetails[BRAND_UID_MAP[this.props.brand]]) {
            Auth.signOut({
                global: true
            }).then(() => {
                // Redirect to brand page
                redirectToChangeAccountPage(null, this.props.translator)
            }).catch((error) => {
                logger.error({
                    appContext: {
                        msg: 'Unexpected error in signing out.',
                        email: this.props.profileEmail,
                        error: JSON.stringify(error)
                    }
                });
            })
        }
    }
    redirectTo404() {
        const url = getCurrentEnvironmentConfig().wwwBaseUrl + '/404';
        window.location.assign(url);
    }

    getBaseName() {
        const match = window.location.hostname.match(/local\.shutterfly/);
        if (match) {
            return '/sfly-sso-fe';
        }
        return '';
    }

    render() {
        const { language, centralConfig } = this.props;
        const{isLandscape}=this.state;
        if(isLandscape){
            return(<div id='landscapeMode' style={{display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh"}}>we wont support landscape mode</div>)
        }
        const enableAmazonRoutes = centralConfig?.enableAmazonOnboarding === true;
        const amazonRoutes = [];
        if (enableAmazonRoutes) {
            amazonRoutes.push({
                path: "/amazonOnboard",
                exact: true,
                render: (props) => {
                    resetLoadTimeOfChunk();
                    const AmazonOnBoarding = loadable(() =>
                    import("./component/amazon/AmazonOnBoardingMothersday")
                    );
                    return (
                    <ToolbarTemplateAmazon isPrimeMember={false}>
                        <AmazonOnBoarding
                        {...props}
                        {...this.props}
                        />
                    </ToolbarTemplateAmazon>
                    );
                }
            });
            amazonRoutes.push({
                path: "/amazon",
                exact: true,
                render: (props) => {
                    resetLoadTimeOfChunk();
                    const AmazonOnBoarding = loadable(() =>
                    import("./component/amazon/AmazonOnBoardingMothersday")
                    );
                    return (
                    <ToolbarTemplateAmazon isPrimeMember={false}>
                        <AmazonOnBoarding
                        {...props}
                        {...this.props}
                        />
                    </ToolbarTemplateAmazon>
                    );
                }
            });
        }

        const urlParams = queryString.parse(window.location.search);
        const validPopupSession = this.state.validSession && urlParams.popupFormSubmittedSuccessfully;
        if ((this.state.loadingSession && !validPopupSession) || (this.state.cssLoading && validPopupSession)) {
            return <div></div>
        } else if (validPopupSession || !this.state.validSession) {
            const newAccountSettings = urlParams && urlParams["newAccountSettings"] === "true";
            return (
                <Router basename={this.getBaseName()}>
                    {/* Imports the theme for the SSO SPA */}
                    <ThemeController theme={this.props.theme}>
                        {/* Main App Routes*/}
                        {
                            this.props.translator ?
                                <div className={`app-container language-${language} ${newAccountSettings ? "new-account-settings-app-container" : ""}`}>
                                    <Switch>
                                        {/* TODO: Get rid of the ...this.props now that everything is in the redux stores */}
                                        <Route path="/" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const SignInWrapper = loadable(() => import('./component/sign-in/SignInWrapper'));
                                            const urlParams = queryString.parse(window.location.search);
                                            const newSignIn = ((urlParams['theme']?.toLowerCase() === 'sfly') || (urlParams['theme']?.toLowerCase() === 'wpd') || !urlParams['theme'] || (urlParams['theme']?.toLowerCase() === 'inc') || (urlParams['theme']?.toLowerCase() === 'tp')) && ((urlParams['brand']?.toLowerCase() === 'sfly') || (urlParams['brand']?.toLowerCase() === 'wpd') || (urlParams['brand']?.toLowerCase() === 'inc') || !urlParams['brand'] || (urlParams['brand']?.toLowerCase() === 'tp'));
                                            return (
                                                <ToolbarTemplate hideHeader={newSignIn}>
                                                    <SignInWrapper {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />

                                        <Route path="/tilesSignin" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const SignInWrapper = loadable(() => import('./component/sign-in/SignInWrapper'));
                                            return (
                                                <ToolbarTemplateGuest {...props} {...this.props} >
                                                    <SignInWrapper {...props} {...this.props} guest />
                                                </ToolbarTemplateGuest>
                                            );
                                        }} />

                                        <Route path="/tilesSignup" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const SignUpWrapper = loadable(() => import('./component/sign-up/SignUpWrapper'));
                                            return (
                                                <ToolbarTemplateGuest {...props} {...this.props} >
                                                    <SignUpWrapper {...props} {...this.props} guest />
                                                </ToolbarTemplateGuest>
                                            );
                                        }} />

                                        <Route path="/tilesHello" exact
                                            render={(props) => {
                                                resetLoadTimeOfChunk();
                                                const GuestPage = loadable(() => import('./component/sign-in/GuestSignIn'));
                                                return (
                                                    <ToolbarTemplateGuest {...props} {...this.props} >
                                                    <GuestPage {...props} {...this.props}
                                                        title='guest.subtitle1'
                                                        subtitle='guest.subtitle2'
                                                        inputName="email"
                                                        validationConstraints={VALIDATION_CONSTRAINTS.Guest2}
                                                            placeholder='guest.emailPlaceholder'
                                                            maxLength={64}

                                                        />
                                                    </ToolbarTemplateGuest>
                                                );
                                            }} />

                                        <Route path="/signup" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const SignUpWrapper = loadable(() => import('./component/sign-up/SignUpWrapper'));
                                            const urlParams = queryString.parse(window.location.search);
                                            const newSignUp = ((urlParams['theme']?.toLowerCase() === 'sfly') || (urlParams['theme']?.toLowerCase() === 'wpd') || !urlParams['theme'] || (urlParams['theme']?.toLowerCase() === 'inc') || (urlParams['theme']?.toLowerCase() === 'tp')) && ((urlParams['brand']?.toLowerCase() === 'sfly') || (urlParams['brand']?.toLowerCase() === 'wpd') || (urlParams['brand']?.toLowerCase() === 'inc') || !urlParams['brand'] || (urlParams['brand']?.toLowerCase() === 'tp'));
                                            return (<>
                                            <ToolbarTemplate hideHeader={newSignUp}>
                                                    <SignUpWrapper {...props} {...this.props} />
                                                </ToolbarTemplate></>
                                            
                                            );
                                        }} />
                                        <Route path="/guestsignup" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const SignUpWrapper = loadable(() => import('./component/sign-up/SignUpWrapper'));
                                            return (
                                                <ToolbarTemplateGuest {...props} {...this.props} >
                                                    <SignUpWrapper {...props} {...this.props} guest />
                                                </ToolbarTemplateGuest>
                                            );
                                        }} />
                                        <Route path="/logout" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const Logout = loadable(() => import('./component/logout/Logout'));
                                            return (<Logout {...props} />)
                                        }} />
                                        <Route path="/elevate" exact render={(props) => {
                                            this.navigateToBrandPage();
                                            resetLoadTimeOfChunk();
                                            const SignInWrapper = loadable(() => import('./component/sign-in/SignInWrapper'));
                                            return (
                                                <ToolbarTemplate>
                                                    <SignInWrapper {...props} {...this.props} currentUserDetails={this.state.userDetails} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/changepassword" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const ChangePassword = loadable(() => import('./component/change-password/ChangePassword'));
                                            return (
                                                <ToolbarTemplate>
                                                    <ChangePassword {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />


                                        <Route path="/forgotPassword" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const ForgotPassword = loadable(() => import('./component/forgot-password/ForgotPassword'));
                                            return (
                                                props.location.state?.guest ?
                                                <ToolbarTemplateGuest {...props} {...this.props} >
                                                    <ForgotPassword {...props} {...this.props} />
                                                    </ToolbarTemplateGuest> :
                                                <ToolbarTemplate>
                                                    <ForgotPassword {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/resetPassword" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const ResetPasswordWrapper = loadable(() => import('./component/reset-password/ResetPasswordWrapper'));
                                            return (
                                                <ToolbarTemplate>
                                                    <ResetPasswordWrapper {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/migrate" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const Migrate = loadable(() => import('./component/migrate/Main'));
                                            return (
                                                <ToolbarTemplate>
                                                    <Migrate {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/migrate/forgotPassword" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const MigrateForgotPassword = loadable(() => import('./component/migrate/ForgotPassword'));
                                            return (
                                                <ToolbarTemplate>
                                                    <MigrateForgotPassword {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/profile" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const ProfileUpdateComponent = loadable(() => import('./component/profile/ProfileUpdate'));
                                            return (
                                                <ToolbarTemplate>
                                                    <ProfileUpdateComponent {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/ccpa" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CustomerProtectionAct = loadable(() => import('./component/sign-in/CustomerProtectionAct'));
                                            return (
                                                <ToolbarTemplate>
                                                    <CustomerProtectionAct {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/migrate/verificationLinkSent" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const VerificationLinkSent = loadable(() => import('./component/migrate/common/VerificationLinkSent'));
                                            return (
                                                <ToolbarTemplate>
                                                    <VerificationLinkSent {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/unlink" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const UnlinkComponent = loadable(() => import('./component/unlink/Unlink'));
                                            return (
                                                <ToolbarTemplate>
                                                    <UnlinkComponent {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/federatedSignIn" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const params = new URLSearchParams(window.location.search);
                                            const provider = params.get('provider') && SOCIAL_PROVIDER_NAME[params.get('provider')] ? params.get('provider') : undefined;
                                            if (!!provider) {
                                                document.body.classList.add('partner-redirect-screen');
                                            } else {
                                                document.body.classList.remove('partner-redirect-screen');
                                            }
                                            const SocialComponent = loadable(() => import('./component/social/Social'));
                                            return (
                                                <ToolbarTemplate>
                                                    <SocialComponent {...props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        <Route path="/social/callback" exact render={(props) => {
                                            const LinkAccount = loadable(() => import('./component/social-callback/SocialCallback'));
                                            return (
                                                <ToolbarTemplate>
                                                    <LinkAccount {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />

                                        <Route path="/linking" render={(props) => {
                                            const LinkAccount = loadable(() => import('./component/social-callback/SocialSignIn'));
                                            return (
                                                <ToolbarTemplate>
                                                    <LinkAccount {...props} {...this.props} />
                                                </ToolbarTemplate>
                                            );
                                        }} />
                                        
                                        <Route path="/requestyourcontact" render={(props) => {
                                            const RequestYourContact = loadable(() => import('./component/requestContact/requestContact'))
                                            return (
                                                <ToolbarTemplateMarketing>
                                                    <RequestYourContact {...props} {...this.props} currentUserDetails={this.state.userDetails} urlParams={this.state.urlParams}/>
                                                </ToolbarTemplateMarketing>
                                            );
                                        }} />

                                        <Route path="/costco" render={(props) => {
                                            const Memebership = loadable(() => import('./component/costco/Membership'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <Memebership {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoMembership" render={(props) => {
                                            const Memebership = loadable(() => import('./component/costco/Membership'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <Memebership {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoVerifyEmail" render={(props) => {
                                            const CostcoVerifyEmail = loadable(() => import('./component/costco/CostcoVerifyEmail'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CostcoVerifyEmail {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoEmailMigration" render={(props) => {
                                            const CostcoVerifyEmailMigrate = loadable(() => import('./component/costco/CostcoVerifyEmail'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CostcoVerifyEmailMigrate {...props} {...this.props} costocNewPath />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />
                                        <Route path="/welcomecostco" render={(props) => {
                                            const WelcomeCostco = loadable(() => import('./component/costco/WelcomeCostco'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <WelcomeCostco {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoVerificationLinkSent" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const VerifyEmail = loadable(() => import('./component/costco/VerifyEmail'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <VerifyEmail {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoCongratsTransfer" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CongratulationsTransferred = loadable(() => import('./component/costco/CongratulationsTransferred'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CongratulationsTransferred {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />



                                        <Route path="/costcoCongrats" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CongratulationLinked = loadable(() => import('./component/costco/CongratulationsLinked'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CongratulationLinked {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoSignUp" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CostcoSignUp = loadable(() => import('./component/costco/CostcoSignUp'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CostcoSignUp {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoHelloSignUp" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CostcoHelloSingUp = loadable(() => import('./component/costco/CostcoHelloSingUp'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CostcoHelloSingUp {...props} {...this.props} />
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />

                                        <Route path="/costcoSignIn" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CostcoSignIn = loadable(() => import('./component/costco/CostcoSignIn'));
                                            return (
                                                <ToolbarTemplateCostco>
                                                    <CostcoSignIn {...props} {...this.props}/>
                                                </ToolbarTemplateCostco>
                                            );
                                        }} />


                                       {amazonRoutes.map((routeConfig) => {
                                        return <Route {...routeConfig}></Route>
                                       })}



                                        <Route path="/amazon/social/callback" render={(props) => {
                                            const LinkAccount = loadable(() => import('./component/amazon/SocialCallback'));
                                            return (
                                                <ToolbarTemplateAmazon isPrimeMember={false}>
                                                    <LinkAccount {...props} {...this.props} />
                                                </ToolbarTemplateAmazon>
                                            );
                                        }} /> 

                                        <Route path="/amazoncongrats" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const AmazonCongrats = loadable(() => import('./component/amazon/amazoncongrats'));
                                            return (
                                                <AmazonCongrats {...props} {...this.props}/>
                                            );
                                        }} />

                                        <Route path="/amazonlinkingprovider" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const AmazonLinkingProvider = loadable(() => import('./component/amazon/AmazonLinkingProvider'));
                                            return (
                                                <ToolbarTemplateAmazon isPrimeMember={false}>
                                                    <AmazonLinkingProvider {...props} {...this.props}/>
                                                </ToolbarTemplateAmazon>
                                            );
                                        }} />
                                        <Route path="/amazonlinking" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const AmazonLinking = loadable(() => import('./component/amazon/amazonlinking'));
                                            return (
                                                <ToolbarTemplateAmazon isPrimeMember={false}>
                                                    <AmazonLinking {...props} {...this.props}/>
                                                </ToolbarTemplateAmazon>
                                            );
                                        }} />

                                        <Route path="/amazononemorething" exact render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const CreateOrSignin = loadable(() => import('./component/amazon/createanaccount'));
                                            return (
                                                <ToolbarTemplateAmazon isPrimeMember={false}>
                                                    <CreateOrSignin {...props} {...this.props}/>
                                                </ToolbarTemplateAmazon>
                                            );
                                        }} />

                                        <Route path="/amazonsignin" render={(props) => {
                                            resetLoadTimeOfChunk();
                                            const AmazonSignin = loadable(() => import('./component/amazon/amazonsignin'));
                                            return (
                                                // <ToolbarTemplateAmazon isPrimeMember={false}>
                                                    <AmazonSignin {...props} {...this.props}/>
                                                // </ToolbarTemplateAmazon>
                                            );
                                        }} />

                                        <Route component={() => { this.redirectTo404() }} />
                                    </Switch>
                                    <SessionExpiryOverlay />
                                </div>
                                : null
                        }
                    </ThemeController>
                </Router>
            );
        }
        const NoMatch = loadable(() => import('./component/no-match/NoMatch'));
        return (
            <NoMatch message={'no_match.unexpected_error'} translator={this.props.translator} />
        );
    }
}


// Redux Setup
const mapStateToProps = (state) => {
    return {
        brand: state.app.brand,
        theme: state.app.theme,
        email: state.app.email,
        cid: state.app.cid,
        redirectUri: state.app.redirectUri,
        verifyguestaccount: state.app.verifyguestaccount,
        language: state.app.language,
        translator: state.app.translator,
        profileEmail: state.app.profileEmail,
    };
};

const mapDispatchToProps = dispatch => ({
    setQueryStringToState: (parsedQueryString) => {
        dispatch(setQueryStringToState(parsedQueryString));
    },
    setUserDetailsToState: (userDetails) => {
        dispatch(setUserDetailsToState(userDetails));
    },
    setDynamicContent: (contentSpaceNames) => {
        dispatch(setDynamicContent(contentSpaceNames));
    },
    setIsChromeIncognito: (bool) => {
        dispatch(setIsChromeIncognito(bool))
    },
    setCentralConfig: (centralConfig) => {
        dispatch(setCentralConfig(centralConfig))
    },
    // LoadABConfigComplexPassword: (complexPassword) => {
    //     dispatch(loadABTestConfigComplexPassword(complexPassword));
    // },
    // LoadABConfigSignUp: (newSignUp) => {
    //     dispatch(loadABTestConfigSignUp(newSignUp));
    // },
    // LoadABConfig: () => {
    //     dispatch(loadABTestConfig());
    // },
  });

export default resolve({
    "awsConfig": (props) => {
        if (window.location.pathname.match(/(\/social\/callback)/)) {
            // Let /social/callback configure AWS
            return {};
        }
        const staySignedIn = window.localStorage.getItem('staySignedIn') === 'true';
        return getAwsConfig(staySignedIn).then((configData) => {
            return { configData: configData };
        }).catch((error) => {
            return { error: error };
        });
    },
    "centralConfig": () => {
        return getCentralConfig();
    }
})(
    connect(mapStateToProps, mapDispatchToProps)(App)
);
